import { Box, Tab, Tabs } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import type { MarketProgram } from '@/features/bidding/constants'
import { BidType } from '@/features/bidding/constants'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import { useHasCapacityBidHistories } from '@/features/bidding/hooks/useHasBidHistories'
import { bidTypeToSlug, marketProgramToSlug } from '@/features/bidding/utils/bidDetail/routeSlugs'
import { MarketDate } from '@/features/bidding/utils/date/marketDate'

type BidDetailTabsProps = Readonly<{
  marketProgram: MarketProgram
  bidType: BidType
}>

/**
 * Tab bar for the new bid detail page. Switches the URL between
 * `/bidding/:marketProgram/energy` and `/bidding/:marketProgram/capacity`
 *
 * Stays mounted across tab changes; the bid-type view swap is driven by
 * the URL re-render.
 */
export default function BidDetailTabs({ marketProgram, bidType }: BidDetailTabsProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { selectedCountry } = useBiddingContext()

  const portfolioCode = searchParams.get('portfolioCode')
  const deliveryDay = searchParams.get('deliveryDay')
  const groupParams =
    portfolioCode && deliveryDay
      ? { marketProgram, portfolioCode, deliveryDay: new MarketDate(deliveryDay), bidType }
      : null

  const { hasBidHistories: hasCapacityBid } = useHasCapacityBidHistories(selectedCountry, groupParams)

  const handleChange = (_: unknown, nextBidType: BidType) => {
    const queryString = searchParams.toString()
    const suffix = queryString ? `?${queryString}` : ''
    navigate(`/bidding/${marketProgramToSlug(marketProgram)}/${bidTypeToSlug(nextBidType)}${suffix}`)
  }

  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
      <Tabs value={bidType} onChange={handleChange}>
        <Tab label={t('bidding.results.energy_bids')} value={BidType.ENERGY} />
        {hasCapacityBid && <Tab label={t('bidding.results.capacity_bids')} value={BidType.CAPACITY} />}
      </Tabs>
    </Box>
  )
}
